import dayjs, { Dayjs } from "dayjs";
import { useLocalStorageState } from "hooks/useLocalStorageState";
import { useCallback, useMemo, useRef, useState } from "react";
import {
  AreaMaterialSnapshot,
  SerializableAreaMaterialSnapshot,
} from "components/scrapEyeLabelling/types";
import { useTenant } from "hooks/settings";

const deserializeSnapshots = (
  snapshots: SerializableAreaMaterialSnapshot[]
): AreaMaterialSnapshot[] =>
  snapshots.map((s) => ({
    timestamp: dayjs.unix(s.timestampUnixSeconds),
    mapping: s.mapping,
  }));

const computeFinalMapping = (
  snapshots: SerializableAreaMaterialSnapshot[]
): Record<string, string> => {
  if (snapshots.length === 0) {
    return {};
  }
  const result: Record<string, string> = {};
  const sortedSnapshots = [...snapshots].sort(
    (a, b) => a.timestampUnixSeconds - b.timestampUnixSeconds
  );
  for (const snapshot of sortedSnapshots) {
    for (const [area, material] of Object.entries(snapshot.mapping)) {
      result[area] = material;
    }
  }
  return result;
};

export const useAreaMaterialMapping = (
  storageKey: string | null,
  defaultAreaMaterials?: Record<string, string>,
  locationKey?: string | null
) => {
  const shouldStoreLocally = storageKey !== null;
  const tenantName = useTenant();

  // Location-level persistent storage for preserving mapping between reservations
  // This stores the last snapshot's mapping from the previous reservation
  // Include tenant name to prevent collisions between tenants with same location names
  const locationMaterialStorageKey =
    locationKey && tenantName
      ? `area-materials-location-${tenantName}-${locationKey}-v1`
      : null;
  // Use a unique disabled key when locationKey is not provided to avoid
  // reading/writing to an empty or conflicting localStorage key
  const [persistentMapping, setPersistentMapping] = useLocalStorageState<
    Record<string, string>
  >(locationMaterialStorageKey ?? "__disabled_persistent_mapping__", {});

  const initialPersistentMappingRef = useRef<Record<string, string> | null>(
    null
  );
  initialPersistentMappingRef.current ??= { ...persistentMapping };

  const areaMaterialStorageKey = `${storageKey ?? ""}-area-materials-v2`;
  const [serializedSnapshots, setSerializedSnapshots] = useLocalStorageState<
    SerializableAreaMaterialSnapshot[]
  >(areaMaterialStorageKey, []);

  const [inMemorySnapshots, setInMemorySnapshots] = useState<
    SerializableAreaMaterialSnapshot[]
  >([]);

  const activeSerializedSnapshots = useMemo(() => {
    return shouldStoreLocally ? serializedSnapshots : inMemorySnapshots;
  }, [shouldStoreLocally, serializedSnapshots, inMemorySnapshots]);

  const mergedDefaults = useMemo(() => {
    if (!locationMaterialStorageKey) {
      return defaultAreaMaterials ?? {};
    }
    return {
      ...(defaultAreaMaterials ?? {}),
      ...(initialPersistentMappingRef.current ?? {}),
    };
  }, [defaultAreaMaterials, locationMaterialStorageKey]);

  // Check if persistent mapping from a previous reservation is being used
  // This is used to show a warning when starting a new reservation
  const isUsingPersistentDefaults = useMemo(() => {
    if (!locationMaterialStorageKey) {
      return false;
    }
    const initialMapping = initialPersistentMappingRef.current ?? {};
    if (Object.keys(initialMapping).length === 0) {
      return false;
    }
    if (activeSerializedSnapshots.length > 0) {
      return false;
    }
    for (const [area, material] of Object.entries(initialMapping)) {
      const serverDefault = defaultAreaMaterials?.[area];
      // If the persistent mapping has a meaningful value (not "unknown")
      // and it differs from what the server provides (or server has nothing),
      // then we're using persistent defaults
      if (material !== "unknown") {
        if (serverDefault === undefined || serverDefault !== material) {
          return true;
        }
      }
    }
    return false;
  }, [
    locationMaterialStorageKey,
    defaultAreaMaterials,
    activeSerializedSnapshots,
  ]);

  const commitPersistentMapping = useCallback(() => {
    if (!shouldStoreLocally || !locationMaterialStorageKey) {
      return;
    }

    if (activeSerializedSnapshots.length > 0) {
      // Start with the current persistent mapping (from previous reservations)
      // then apply this reservation's snapshots on top
      const newChanges = computeFinalMapping(activeSerializedSnapshots);
      setPersistentMapping((prev) => ({
        ...prev,
        ...newChanges,
      }));
    }
  }, [
    shouldStoreLocally,
    locationMaterialStorageKey,
    activeSerializedSnapshots,
    setPersistentMapping,
  ]);

  const areaMaterialSnapshots = useMemo(
    () => deserializeSnapshots(activeSerializedSnapshots),
    [activeSerializedSnapshots]
  );

  const getAreaMaterialMappingAtTime = useCallback(
    (timestamp: Dayjs): Record<string, string> => {
      const targetUnix = timestamp.unix();
      const result: Record<string, string> = { ...mergedDefaults };

      const relevantSnapshots = activeSerializedSnapshots.filter(
        (s) => s.timestampUnixSeconds <= targetUnix
      );

      const sortedSnapshots = [...relevantSnapshots].sort(
        (a, b) => a.timestampUnixSeconds - b.timestampUnixSeconds
      );

      for (const snapshot of sortedSnapshots) {
        for (const [area, material] of Object.entries(snapshot.mapping)) {
          result[area] = material;
        }
      }

      return result;
    },
    [activeSerializedSnapshots, mergedDefaults]
  );

  const updateAreaMaterialMapping = useCallback(
    (timestamp: Dayjs, area: string, material: string | null) => {
      const targetUnix = timestamp.unix();

      const updateFn = (
        prev: SerializableAreaMaterialSnapshot[]
      ): SerializableAreaMaterialSnapshot[] => {
        const existingIndex = prev.findIndex(
          (s) => s.timestampUnixSeconds === targetUnix
        );

        if (existingIndex !== -1) {
          return prev
            .map((snapshot, index) => {
              if (index === existingIndex) {
                if (material === null) {
                  // eslint-disable-next-line @typescript-eslint/no-unused-vars
                  const { [area]: _removed, ...rest } = snapshot.mapping;
                  return { ...snapshot, mapping: rest };
                }
                return {
                  ...snapshot,
                  mapping: { ...snapshot.mapping, [area]: material },
                };
              }
              return snapshot;
            })
            .filter((s) => Object.keys(s.mapping).length > 0);
        }

        if (material === null) {
          return prev;
        }

        return [
          ...prev,
          { timestampUnixSeconds: targetUnix, mapping: { [area]: material } },
        ];
      };

      if (shouldStoreLocally) {
        setSerializedSnapshots(updateFn);
      } else {
        setInMemorySnapshots(updateFn);
      }
    },
    [shouldStoreLocally, setSerializedSnapshots]
  );

  const deleteAreaMaterialSnapshot = useCallback(
    (timestamp: Dayjs) => {
      const targetUnix = timestamp.unix();

      const updateFn = (
        prev: SerializableAreaMaterialSnapshot[]
      ): SerializableAreaMaterialSnapshot[] =>
        prev.filter((s) => s.timestampUnixSeconds !== targetUnix);

      if (shouldStoreLocally) {
        setSerializedSnapshots(updateFn);
      } else {
        setInMemorySnapshots(updateFn);
      }
    },
    [shouldStoreLocally, setSerializedSnapshots]
  );

  const getAreaMaterialMappingBeforeTime = useCallback(
    (timestamp: Dayjs): Record<string, string> => {
      const targetUnix = timestamp.unix();
      const result: Record<string, string> = { ...mergedDefaults };

      const relevantSnapshots = activeSerializedSnapshots.filter(
        (s) => s.timestampUnixSeconds < targetUnix
      );

      const sortedSnapshots = [...relevantSnapshots].sort(
        (a, b) => a.timestampUnixSeconds - b.timestampUnixSeconds
      );

      for (const snapshot of sortedSnapshots) {
        for (const [area, material] of Object.entries(snapshot.mapping)) {
          result[area] = material;
        }
      }

      return result;
    },
    [activeSerializedSnapshots, mergedDefaults]
  );

  return useMemo(
    () => ({
      areaMaterialSnapshots,
      getAreaMaterialMappingAtTime,
      getAreaMaterialMappingBeforeTime,
      updateAreaMaterialMapping,
      deleteAreaMaterialSnapshot,
      isUsingPersistentDefaults,
      commitPersistentMapping,
      defaultAreaMaterials: defaultAreaMaterials ?? {},
    }),
    [
      areaMaterialSnapshots,
      getAreaMaterialMappingAtTime,
      getAreaMaterialMappingBeforeTime,
      updateAreaMaterialMapping,
      deleteAreaMaterialSnapshot,
      isUsingPersistentDefaults,
      commitPersistentMapping,
      defaultAreaMaterials,
    ]
  );
};
